import { captureException } from "@sentry/browser";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft } from "react-feather";
import { toast } from "react-toastify";
import { trackingEventDictionary } from "../../../core/dictionary/track-events";
import LocalStorageService from "../../../core/services/local-storage.service";
import { generateTrackingObject, trademoTrackingEvents } from "../../../core/utility/trademo-tracking";
import { getAccountsUrl } from "../../../utils/accountsUrl";
import { getApiUrl } from "../../../utils/baseUrl";
import { globalGetApiUrl } from "../../../utils/globalBaseUrl";
import { getIntelUrl } from "../../../utils/intelUrl";
import { getSourceAtlasUrl } from "../../../utils/sourceAtlasUrl";
import { getPaymentsUrl } from "../../../utils/paymentsUrl";
import { getTradeComplianceUrl } from "../../../utils/tradeComplianceUrl";
import { getTradeMapUrlValue } from "../../../utils/tradeMapUrl";
import { getUmsRedirectionUrl } from "../../../utils/umsRedirectUrl";
import UserAvatar from "../../UserAvatar";
import { getFinScreenerUrl } from "../../../utils/tradescreenUrl";
import { fetchUserProfile } from "../../../redux/slices/userSlice";

const localStorageService = LocalStorageService.getService();

export const intelHeader = {
  logoutUser: globalGetApiUrl("auth/logout"),
  profile: globalGetApiUrl("auth/getUserProfileComplete"),
  planDetails: getApiUrl("getUserCreditsByPlan"),
};

const buildUrlParams = () => {
  return `T=${localStorageService.getAccessToken()}&I=${localStorageService.getSessionID()}&D=${localStorageService.getSessionExpiryDate()}`;
};

const navigateTo = (url) => {
  window.location.href = url;
};

export default function AccountHeader({ type }) {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.user);

  const [currentProduct, setCurrentProduct] = useState("");
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  useEffect(() => {
    const storedProduct = localStorageService.getUserProduct();
    setCurrentProduct(storedProduct || "");
    dispatch(fetchUserProfile());
  }, []);

  useEffect(() => {
    hydrateAdminStatus();
  }, [profile]);

  const hydrateAdminStatus = () => {
    const isInternalAdmin = JSON.parse(localStorage.getItem("isInternalAdmin"));
    const isInternalUser = JSON.parse(localStorage.getItem("isInternalUser"));
    const isAccountAdmin = JSON.parse(localStorage.getItem("isAdmin"));

    setIsAdminUser(isInternalAdmin || isInternalUser || isAccountAdmin || false);
  };

  const handleLogout = async () => {
    trademoTrackingEvents(trackingEventDictionary.headerUserProfileSignOut, generateTrackingObject());

    try {
      const response = await axios.get(intelHeader.logoutUser);

      if (response.data.status?.toUpperCase() === "SUCCESS") {
        localStorageService.clearCompleteStorage();
        navigateTo(getAccountsUrl() + "signout");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      captureException(error);
    }
  };

  const redirectToIntel = () => {
    navigateTo(`${getIntelUrl()}intel?${buildUrlParams()}&Type=D&P=${currentProduct}&Email=${profile.email}`);
  };

  const redirectToSourceAtlas = () => {
    navigateTo(`${getSourceAtlasUrl()}intel?${buildUrlParams()}&Type=D&P=${currentProduct}&Email=${profile.email}`);
  };

  const redirectToGTM = () => {
    navigateTo(`${getTradeComplianceUrl()}?${buildUrlParams()}&P=trade-compliance&Email=${profile.email}`);
  };

  const redirectToTrademap = () => {
    navigateTo(`${getTradeMapUrlValue()}?${buildUrlParams()}&P=trade-map&Email=${profile.email}`);
  };

  const redirectToTradescreen = () => {
    navigateTo(`${getFinScreenerUrl()}?${buildUrlParams()}&P=tradescreen&Email=${profile.email}`);
  };

  const redirectToUMS = () => {
    navigateTo(
      `${getUmsRedirectionUrl()}?${buildUrlParams()}&P=ums-dashboard` +
        `&UserId=${localStorage.getItem("userId")}` +
        `&OrganisationId=${localStorage.getItem("orgId")}` +
        `&isInternalAdmin=${localStorage.getItem("isInternalAdmin")}` +
        `&isInternalUser=${localStorage.getItem("isInternalUser")}` +
        `&isAdmin=${localStorage.getItem("isAdmin")}` +
        localStorage.getItem("productArr")
    );
  };

  const shouldShowMapBackButton = currentProduct === "trademap";
  const shouldShowGTMBackButton = currentProduct === "gtm";
  const shouldShowUMSBackButton = currentProduct === "ums-dashboard";
  const shouldShowTradecreenBackButton = currentProduct.includes("tradescreen");
  const shouldShowIntelBackButton = currentProduct.includes("intel-");
  const shouldShowSourceAtlasBackButton = currentProduct === "source_atlas";

  return (
    <nav className="tw-w-full tw-fixed tw-top-0 tw-left-0 tw-bg-white tw-text-gray-900 tw-border-b tw-border-b-gray-200 tw-z-50">
      <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-h-12">
        {/* Logo + Back Buttons */}
        <div className="tw-flex tw-items-center">
          <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1645085709/global/trademo_logo/justLogo_mr5y0j.svg" alt="Logo" className="tw-h-7" />

          {type !== "no-product-assigned" && (
            <div className="tw-flex tw-items-center tw-gap-5 tw-ml-6 tw-text-sm">
              {shouldShowMapBackButton && (
                <button onClick={redirectToTrademap} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to Map
                </button>
              )}

              {shouldShowTradecreenBackButton && (
                <button onClick={redirectToTradescreen} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to TradeScreen
                </button>
              )}

              {shouldShowGTMBackButton && (
                <button onClick={redirectToGTM} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to GTM
                </button>
              )}

              {shouldShowUMSBackButton && (
                <button onClick={redirectToUMS} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to User Management
                </button>
              )}

              {shouldShowIntelBackButton && (
                <button onClick={redirectToIntel} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to Intel
                </button>
              )}

              {shouldShowSourceAtlasBackButton && (
                <button onClick={redirectToSourceAtlas} className="tw-flex tw-items-center tw-gap-1 hover:tw-opacity-80 tw-text-gray-900">
                  <ArrowLeft size={16} /> Go back to Source Atlas
                </button>
              )}
            </div>
          )}
        </div>

        {/* User Dropdown */}
        <div className="tw-relative" ref={dropdownRef}>
          <div onClick={() => setIsDropdownOpen(true)} className="tw-cursor-pointer">
            <UserAvatar name={profile.fullName || "User"} showName={false} />
          </div>

          {isDropdownOpen && (
            <div className="tw-absolute tw-bg-white tw-rounded-lg tw-top-11 tw-right-0 tw-border tw-shadow-lg tw-border-gray-200 tw-min-w-56 tw-overflow-hidden">
              <div className="tw-flex tw-gap-2 tw-px-3 tw-py-2 tw-border-b">
                <UserAvatar name={profile.fullName} showName={false} />
                <div>
                  <div className="tw-text-label-md">{profile.fullName}</div>
                  <div className="tw-text-label-sm">{profile.email}</div>
                </div>
              </div>
              <div
                className="tw-text-label-md hover:tw-bg-primary-purple-25 tw-px-3 tw-py-2 tw-cursor-pointer"
                onClick={() => navigateTo(`${getPaymentsUrl()}payment?${buildUrlParams()}&type=plans`)}
              >
                Plan Details
              </div>
              {isAdminUser && (
                <div className="tw-text-label-md hover:tw-bg-primary-purple-25 tw-px-3 tw-py-2 tw-cursor-pointer tw-border-t" onClick={redirectToUMS}>
                  User Management System
                </div>
              )}
              <div className="tw-text-label-md hover:tw-bg-primary-purple-25 tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-border-t" onClick={handleLogout}>
                <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1707728252/intel/icons/xkb7yvj12tnggbc5ptve.svg" alt=" logout icon" />
                Sign Out
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
